// Phase 4 — Install affordance composable.
//
// Captures the browser's `beforeinstallprompt` event so the app can offer a
// styled install button on mobile. Persists dismissal in
// useFiltersStore.installPromptDismissed so we don't re-prompt every visit.
// Module-level singleton state (NOT window.*) per Phase 1 directive #7.
//
// Lifecycle:
//   - On `setup`: subscribe to window beforeinstallprompt + appinstalled.
//   - When event fires: stash it, set canInstall true (unless dismissed).
//   - User clicks "Install": call event.prompt(), await userChoice, persist.
//   - User explicitly dismisses: set persisted flag, hide button forever.
//
// Tests live in useInstallPrompt.test.ts and exercise the state transitions
// without needing a real beforeinstallprompt (synthetic dispatch).

import { onBeforeUnmount, onMounted, readonly, ref } from 'vue';
import { useFiltersStore } from '@/stores/filters';
import { log } from '@/lib/log';

interface BeforeInstallPromptEventLike extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let storedEvent: BeforeInstallPromptEventLike | null = null;
const canInstall = ref(false);
let listenersInstalled = 0;

function onBeforeInstallPrompt(filters: ReturnType<typeof useFiltersStore>) {
    return (event: Event) => {
        event.preventDefault();
        storedEvent = event as BeforeInstallPromptEventLike;
        if (filters.data.installPromptDismissed) {
            log.debug('install_prompt.captured_but_dismissed');
            return;
        }
        canInstall.value = true;
        log.info('install_prompt.captured');
    };
}

function onAppInstalled(filters: ReturnType<typeof useFiltersStore>) {
    return () => {
        canInstall.value = false;
        storedEvent = null;
        filters.patch({ installPromptDismissed: true });
        log.info('install_prompt.app_installed');
    };
}

export function useInstallPrompt() {
    const filters = useFiltersStore();

    let beforeHandler: ((e: Event) => void) | null = null;
    let installedHandler: (() => void) | null = null;

    onMounted(() => {
        beforeHandler = onBeforeInstallPrompt(filters);
        installedHandler = onAppInstalled(filters);
        window.addEventListener('beforeinstallprompt', beforeHandler);
        window.addEventListener('appinstalled', installedHandler);
        listenersInstalled++;
    });

    onBeforeUnmount(() => {
        if (beforeHandler) window.removeEventListener('beforeinstallprompt', beforeHandler);
        if (installedHandler) window.removeEventListener('appinstalled', installedHandler);
        listenersInstalled--;
    });

    async function installApp(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
        if (!storedEvent) {
            log.warn('install_prompt.invoked_without_event');
            return 'unavailable';
        }
        try {
            await storedEvent.prompt();
            const choice = await storedEvent.userChoice;
            log.info('install_prompt.choice', { outcome: choice.outcome, platform: choice.platform });
            // Either outcome means we shouldn't show the button again.
            filters.patch({ installPromptDismissed: true });
            canInstall.value = false;
            storedEvent = null;
            return choice.outcome;
        } catch (err) {
            log.warn('install_prompt.failed', { error: (err as Error).message });
            return 'unavailable';
        }
    }

    function dismiss() {
        filters.patch({ installPromptDismissed: true });
        canInstall.value = false;
        storedEvent = null;
        log.info('install_prompt.dismissed');
    }

    return {
        canInstall: readonly(canInstall),
        installApp,
        dismiss,
    };
}

// Test-only — drains module-level state between tests (Phase 2.6 retro #5).
export function _resetInstallPromptForTests() {
    storedEvent = null;
    canInstall.value = false;
    listenersInstalled = 0;
}
